import React, {FC, memo, useCallback, ReactNode} from "react";
import {useDispatch, useSelector} from "react-redux";
import {reviewActions} from "../review.slice";
import {reviewActionBarSelector} from "./reviewActionBarSelector";
import styled from "styled-components";
import { ThemeProps } from "../../styles/theme";
import { styleConstants } from "../../styles/styleConstants";
import { uiActions } from "../../ui/ui.slice";
import { ExtraActionsMenu } from "./extraActionsMenu/extraActionsMenu";

interface ReviewActionBarProps {
    children?: ReactNode;
}

const iconToFlag = (icon: string): string => {
    if (icon === 'en') return '🇬🇧';
    if (icon === 'gr' || !icon) return '🇬🇷';
    return '';
};

const Root = styled.div<ThemeProps>`
    background-color: ${props => props.theme.secondaryBackground};
    border: ${props => props.theme.secondaryBorder} 1px solid;
    position: sticky;
    top: 0;
    height: ${styleConstants.actionBarHeight};
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0 10px;
    z-index: 10;
`;

const LeftSection = styled.div`
    display: flex;
    align-items: center;
    width: 40px;
`;

const CenterSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
`;

const RightSection = styled.div`
    display: flex;
    align-items: center;
    width: 40px;
`;

const Icon = styled.svg<ThemeProps>`
    height: ${styleConstants.actionBarIconSize};
    width: ${styleConstants.actionBarIconSize};
    fill: ${props => props.theme.secondaryIcon};
    cursor: pointer;
`;

const ProgressText = styled.div<ThemeProps>`
    color: ${props => props.theme.secondaryIcon};
    font-size: 0.9em;
`;

const ProgressColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.2;
`;

const GroupProgressText = styled.div<ThemeProps>`
    color: ${props => props.theme.secondaryIcon};
    font-size: 0.72em;
    opacity: 0.85;
`;

const LanguageFlag = styled.span`
    font-size: 1.1em;
    margin-right: 8px;
    line-height: 1;
`;

const ReviewActionBarComponent: FC<ReviewActionBarProps> = (props) => {
    const stateProps = useSelector(reviewActionBarSelector);
    const dispatch = useDispatch();

    const openMenuHandler = useCallback(() => {
        dispatch(uiActions.openSideMenu());
    }, [dispatch]);

    const openExtraActionsMenuHandler = useCallback(() => {
        dispatch(reviewActions.openExtraActionsMenu());
    }, [dispatch]);

    return (
        <Root data-testid="review-action-bar">
            <LeftSection>
                <Icon data-testid="review-menu-button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" onClick={openMenuHandler}>
                    <path d="M0 0h24v24H0z" fill="none"/>
                    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                </Icon>
            </LeftSection>
            <CenterSection>
                {iconToFlag(stateProps.icon) && (
                    <LanguageFlag data-testid="review-language-icon">{iconToFlag(stateProps.icon)}</LanguageFlag>
                )}
                <ProgressColumn>
                    <ProgressText data-testid="review-progress">
                        {stateProps.cardNumberInReview} / {stateProps.totalCardsForReview}
                    </ProgressText>
                    {stateProps.groupProgress && (
                        <GroupProgressText data-testid="review-group-progress">
                            Groups: {stateProps.groupProgress.current} / {stateProps.groupProgress.total}
                        </GroupProgressText>
                    )}
                </ProgressColumn>
            </CenterSection>
            <RightSection>
                <Icon data-testid="review-actions-button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" onClick={openExtraActionsMenuHandler}>
                    <path d="M0 0h24v24H0z" fill="none"/>
                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                </Icon>
            </RightSection>
            <ExtraActionsMenu />
            {props.children}
        </Root>
    );
};

export const ReviewActionBar: FC<ReviewActionBarProps> = memo(ReviewActionBarComponent);
ReviewActionBar.displayName = 'ReviewActionBar';
